/* eslint-disable react-hooks/exhaustive-deps */
import React,{useState,useEffect,useCallback}from'react';
import{useNavigate}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import{fmtDate,fmtDateTime}from'../utils/helpers';
import { isShaCoveredPatient } from '../utils/shaPatient';

// ── Constants ───────────────────────────────────────────────────────────────
const WARD_TYPES=['GENERAL','ICU','MATERNITY','PAEDIATRIC','SURGICAL','ORTHOPAEDIC','CARDIOLOGY','NEUROLOGY','ONCOLOGY','EMERGENCY','PRIVATE','SEMI_PRIVATE','DAY_CARE'];
const BED_TYPES=['GENERAL','SEMI_PRIVATE','PRIVATE','ICU','HDU','ISOLATION','RECOVERY','DAY_CARE'];
const BED_STATUSES=['AVAILABLE','OCCUPIED','MAINTENANCE','RESERVED','CLEANING'];
const ADMISSION_TYPES=['PLANNED','EMERGENCY','TRANSFER','MATERNITY','SURGICAL','MEDICAL','PAEDIATRIC'];
const NOTE_TYPES=['PROGRESS','ADMISSION','DISCHARGE','NURSING','CONSULTATION','PROCEDURE'];

// ── Tailwind constants ───────────────────────────────────────────────────────
const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';
const BTN_PRI='bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SEC='bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50';

// ── Helpers ──────────────────────────────────────────────────────────────────
const BASE=process.env.REACT_APP_API_URL;
const hdr=()=>({'Content-Type':'application/json','Authorization':`Bearer ${localStorage.getItem('accessToken')||''}`});

const bedColor=(s:string)=>{
  const m:Record<string,string>={AVAILABLE:'bg-emerald-100 text-emerald-700 border-emerald-200',OCCUPIED:'bg-red-100 text-red-700 border-red-200',MAINTENANCE:'bg-orange-100 text-orange-700 border-orange-200',RESERVED:'bg-blue-100 text-blue-700 border-blue-200',CLEANING:'bg-yellow-100 text-yellow-700 border-yellow-200'};
  return m[s]||'bg-slate-100 text-slate-600 border-slate-200';
};
const statusBadge=(s:string)=>{
  const m:Record<string,string>={ADMITTED:'bg-emerald-100 text-emerald-700',DISCHARGED:'bg-slate-100 text-slate-500',PENDING_DISCHARGE:'bg-amber-100 text-amber-700',TRANSFERRED:'bg-blue-100 text-blue-700',EXPIRED:'bg-red-100 text-red-700'};
  return m[s]||'bg-slate-100 text-slate-500';
};

const Field=({label,children,required,col2}:any)=>(
  <div className={col2?'col-span-2':''}>
    <label className={LBL}>{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>
    {children}
  </div>
);

const Modal=({open,onClose,title,children,size='md'}:any)=>{
  if(!open)return null;
  const w={sm:'max-w-sm',md:'max-w-lg',lg:'max-w-2xl',xl:'max-w-4xl'}[size]||'max-w-lg';
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}/>
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${w} max-h-[90vh] flex flex-col overflow-hidden`}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#02357A] to-[#015CA6] flex-shrink-0">
          <h3 className="font-bold text-white text-lg">{title}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl font-bold leading-none">✕</button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ── Default forms ────────────────────────────────────────────────────────────
const DEF_WARD={wardCode:'',name:'',wardType:'GENERAL',totalBeds:'',floor:'',description:'',isActive:true,dailyRate:''};
const DEF_BED={bedNumber:'',wardId:'',bedType:'GENERAL',status:'AVAILABLE',floor:'',roomNumber:'',dailyRate:'',notes:'',isActive:true};
const DEF_NOTE={admissionId:'',noteType:'PROGRESS',subjective:'',objective:'',assessment:'',plan:'',noteText:'',shift:'MORNING'};
const DEF_ORDER={admissionId:'',patientId:'',orderType:'MEDICATION',priority:'ROUTINE',details:{},notes:'',scheduledAt:''};

export default function IPDPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const[tab,setTab]=useState<'admissions'|'beds'|'wards'|'bulk'|'rounds'|'orders'|'notes'|'transfers'>('admissions');
  const[admissions,setAdmissions]=useState<any[]>([]);
  const[wards,setWards]=useState<any[]>([]);
  const[beds,setBeds]=useState<any[]>([]);
  const[stats,setStats]=useState<any>({});
  const[selWard,setSelWard]=useState('');
  const[search,setSearch]=useState('');
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState('');
  const[shaStatuses,setShaStatuses]=useState<Record<string,any>>({});

  // Selected admission for notes/orders/transfers
  const[selAdmission,setSelAdmission]=useState<any>(null);
  const[rounds,setRounds]=useState<any[]>([]);
  const[notes,setNotes]=useState<any[]>([]);
  const[orders,setOrders]=useState<any[]>([]);
  const[transfers,setTransfers]=useState<any[]>([]);

  // Ward form
  const[showWardForm,setShowWardForm]=useState(false);
  const[editWard,setEditWard]=useState<any>(null);
  const[wf,setWf]=useState({...DEF_WARD});

  // Bed form
  const[showBedForm,setShowBedForm]=useState(false);
  const[editBed,setEditBed]=useState<any>(null);
  const[bf,setBf]=useState({...DEF_BED});

  // Bulk form
  const[bulkWardId,setBulkWardId]=useState('');
  const[bulkPrefix,setBulkPrefix]=useState('');
  const[bulkStart,setBulkStart]=useState('1');
  const[bulkCount,setBulkCount]=useState('10');
  const[bulkType,setBulkType]=useState('GENERAL');
  const[bulkCharge,setBulkCharge]=useState('');

  // Note/Round form
  const[showNoteForm,setShowNoteForm]=useState(false);
  const[nf,setNf]=useState({...DEF_NOTE});

  // Order form
  const[showOrderForm,setShowOrderForm]=useState(false);
  const[of,setOf]=useState<{orderType:string;priority:string;notes:string;details:Record<string,any>}>({orderType:'MEDICATION',priority:'ROUTINE',notes:'',details:{itemName:'',dosage:'',route:'Oral',frequency:'BD',duration:'5 Days'}});
  const[masterDrugs,setMasterDrugs]=useState<any[]>([]);
  const[masterLab,setMasterLab]=useState<any[]>([]);
  const[masterRad,setMasterRad]=useState<any[]>([]);
  const[masterProc,setMasterProc]=useState<any[]>([]);
  const[masterDiet,setMasterDiet]=useState<any[]>([]);
  const[drugSearch,setDrugSearch]=useState('');
  const[orderSearch,setOrderSearch]=useState('');
  const[masterLoaded,setMasterLoaded]=useState(false);

  const NURSING_ORDERS=[
    'Strict Bed Rest','Bed Rest with Bathroom Privileges',
    'Vital Signs Q1H','Vital Signs Q2H','Vital Signs Q4H','Vital Signs Q6H','Vital Signs Q8H',
    'SpO2 Continuous Monitoring','Blood Sugar Monitoring QID','Input/Output Chart (Strict)',
    'Daily Weight','Urine Output Hourly','Wound Dressing Daily','Wound Dressing BD',
    'Catheter Care','Nasogastric Tube Care','Oral Hygiene BD','Sponge Bath Daily',
    'Pressure Sore Prevention','Position Change Q2H','Head End Elevation 30°',
    'Fall Precautions','Deep Breathing Exercises','Incentive Spirometry',
    'Blood Sample Collection','Urine Sample Collection','IV Line Care',
    'Oxygen Inhalation','Nebulization','Cold Fomentation','Hot Fomentation',
  ];

  const loadMasters=async()=>{
    if(masterLoaded)return;
    const[drugs,lab,rad,proc,diet]=await Promise.all([
      api.getDrugs({take:200}).catch(()=>({data:{}})),
      api.getLabTests({take:200}).catch(()=>({data:[]})),
      api.getRadiologyServices({take:100}).catch(()=>({data:[]})),
      api.getProcedureCatalog({take:100}).catch(()=>({data:[]})),
      api.getDietTypes().catch(()=>({data:[]})),
    ]);
    setMasterDrugs(Array.isArray(drugs.data)?drugs.data:drugs.data?.drugs||drugs.data?.data||[]);
    setMasterLab(Array.isArray(lab.data)?lab.data:lab.data?.tests||lab.data?.data||[]);
    setMasterRad(Array.isArray(rad.data)?rad.data:rad.data?.services||rad.data?.data||[]);
    setMasterProc(Array.isArray(proc.data)?proc.data:proc.data?.procedures||proc.data?.data||[]);
    setMasterDiet(Array.isArray(diet.data)?diet.data:diet.data?.types||diet.data?.data||[]);
    setMasterLoaded(true);
  };

  // Transfer form
  const[showTransferForm,setShowTransferForm]=useState(false);
  const[tf,setTf]=useState({toWardId:'',toBedId:'',transferReason:'',transferType:'INTERNAL'});
  const[availBeds,setAvailBeds]=useState<any[]>([]);

  // Status filter
  const[statusFilter,setStatusFilter]=useState('ADMITTED');

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadAll=useCallback(async()=>{
    setLoading(true);
    try{
      const[adm,wrd,bds,st]=await Promise.all([
        fetch(`${BASE}/ipd/admissions?take=100`,{headers:hdr()}).then(r=>r.json()).catch(()=>({})),
        fetch(`${BASE}/ipd/wards`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
        fetch(`${BASE}/ipd/beds`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
        fetch(`${BASE}/ipd/stats`,{headers:hdr()}).then(r=>r.json()).catch(()=>({})),
      ]);
      const admList=adm?.admissions||adm?.data||adm?.rows||(Array.isArray(adm)?adm:[]);
      const wrdList=Array.isArray(wrd)?wrd:wrd?.data||wrd?.wards||[];
      const bdsList=Array.isArray(bds)?bds:bds?.data||bds?.beds||[];
      setAdmissions(admList);
      setWards(wrdList);
      setBeds(bdsList);

      // SHA status badges — shaStatuses used to be populated by nothing at all (dead
      // state variable), so every admission row always showed "No SHA" / "+ SHA Claim"
      // regardless of whether a claim actually existed. Fetch real bulk status now that
      // the backend auto-creates SHA claims on admission.
      const admIds=admList.map((a:any)=>a.id).filter(Boolean);
      if(admIds.length){
        fetch(`${BASE}/sha/status/bulk`,{method:'POST',headers:{...hdr(),'Content-Type':'application/json'},body:JSON.stringify({admissionIds:admIds})})
          .then(r=>r.json()).then(r=>setShaStatuses(r||{})).catch(()=>{});
      }

      setStats(st?.data||st||{
        admitted:admList.filter((x:any)=>x.status==='ADMITTED').length,
        pendingDischarge:admList.filter((x:any)=>x.status==='PENDING_DISCHARGE').length,
        today:admList.filter((x:any)=>(x.admissionDate||x.admission_date||'').startsWith(new Date().toISOString().split('T')[0])).length,
        icu:admList.filter((x:any)=>(x.wardType||x.ward_type||'').includes('ICU')).length,
        totalBeds:bdsList.length,
        availBeds:bdsList.filter((x:any)=>x.status==='AVAILABLE').length,
      });
    }catch{}finally{setLoading(false);}
  },[]);

  const loadBeds=useCallback(async(wardId:string)=>{
    const r=await fetch(`${BASE}/ipd/beds?wardId=${wardId}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]);
    setBeds(Array.isArray(r)?r:r?.data||r?.beds||[]);
  },[]);

  const loadAdmissionData=useCallback(async(aid:string)=>{
    const[rds,nts,ords,trs]=await Promise.all([
      fetch(`${BASE}/ipd/rounds/${aid}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/ipd/notes/${aid}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/ipd/orders/${aid}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/ipd/transfers/${aid}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
    ]);
    setRounds(Array.isArray(rds)?rds:rds?.data||[]);
    setNotes(Array.isArray(nts)?nts:nts?.data||[]);
    setOrders(Array.isArray(ords)?ords:ords?.data||[]);
    setTransfers(Array.isArray(trs)?trs:trs?.data||[]);
  },[]);

  useEffect(()=>{loadAll();},[]);
  useEffect(()=>{if(selWard)loadBeds(selWard);},[selWard]);
  useEffect(()=>{if(selAdmission?.id)loadAdmissionData(selAdmission.id);},[selAdmission]);

  // ── Ward CRUD ─────────────────────────────────────────────────────────────
  const saveWard=async()=>{
    if(!wf.wardCode||!wf.name)return notify('⚠️ Ward code and name are required');
    setSaving(true);
    const payload={wardCode:wf.wardCode.toUpperCase(),name:wf.name,wardType:wf.wardType,totalBeds:+wf.totalBeds||0,floor:wf.floor||null,description:wf.description||null,isActive:wf.isActive,dailyRate:wf.dailyRate?+wf.dailyRate:null};
    const url=editWard?`${BASE}/ipd/wards/${editWard.id}`:`${BASE}/ipd/wards`;
    const r=await fetch(url,{method:editWard?'PUT':'POST',headers:hdr(),body:JSON.stringify(payload)});
    if(r.ok){notify(editWard?'✅ Ward updated':'✅ Ward created');setShowWardForm(false);setEditWard(null);setWf({...DEF_WARD});loadAll();}
    else{const e=await r.json().catch(()=>({}));notify('❌ '+(e.message||'Failed'));}
    setSaving(false);
  };

  const deleteWard=async(id:string)=>{
    if(!window.confirm('Delete this ward? Beds must be empty.'))return;
    const r=await fetch(`${BASE}/ipd/wards/${id}`,{method:'DELETE',headers:hdr()});
    if(r.ok)notify('✅ Ward deleted');else notify('❌ Cannot delete — beds may still be assigned');
    loadAll();
  };

  // ── Bed CRUD ──────────────────────────────────────────────────────────────
  const saveBed=async()=>{
    if(!bf.bedNumber||!bf.wardId)return notify('⚠️ Bed number and ward required');
    setSaving(true);
    const ward=wards.find((w:any)=>w.id===bf.wardId);
    const payload={bedNumber:bf.bedNumber,wardId:bf.wardId,wardName:ward?.name||ward?.wardName||'',bedType:bf.bedType,status:bf.status,floor:bf.floor||null,roomNumber:bf.roomNumber||null,dailyRate:bf.dailyRate?+bf.dailyRate:0,notes:bf.notes||null,isActive:bf.isActive};
    const url=editBed?`${BASE}/ipd/beds/${editBed.id}`:`${BASE}/ipd/beds`;
    const r=await fetch(url,{method:editBed?'PUT':'POST',headers:hdr(),body:JSON.stringify(payload)});
    if(r.ok){notify(editBed?'✅ Bed updated':'✅ Bed created');setShowBedForm(false);setEditBed(null);setBf({...DEF_BED});if(selWard)loadBeds(selWard);else loadAll();}
    else{const e=await r.json().catch(()=>({}));notify('❌ '+(e.message||'Failed'));}
    setSaving(false);
  };

  const updateBedStatus=async(id:string,status:string)=>{
    await fetch(`${BASE}/ipd/beds/${id}/status`,{method:'PUT',headers:hdr(),body:JSON.stringify({status})});
    notify('✅ Status updated');if(selWard)loadBeds(selWard);else loadAll();
  };

  const deleteBed=async(id:string)=>{
    if(!window.confirm('Delete bed?'))return;
    await fetch(`${BASE}/ipd/beds/${id}`,{method:'DELETE',headers:hdr()});
    notify('✅ Bed deleted');if(selWard)loadBeds(selWard);else loadAll();
  };

  // ── Bulk Create ───────────────────────────────────────────────────────────
  const bulkCreate=async()=>{
    if(!bulkWardId)return notify('⚠️ Select ward first');
    const count=+bulkCount||10;const start=+bulkStart||1;
    const ward=wards.find((w:any)=>w.id===bulkWardId);
    const prefix=bulkPrefix||(ward?.wardCode||ward?.ward_code||'BED');
    notify(`⏳ Creating ${count} beds...`);
    let ok=0;
    for(let i=start;i<start+count;i++){
      const r=await fetch(`${BASE}/ipd/beds`,{method:'POST',headers:hdr(),body:JSON.stringify({bedNumber:`${prefix}-${String(i).padStart(2,'0')}`,bedType:bulkType,wardId:bulkWardId,wardName:ward?.name||'',status:'AVAILABLE',isActive:true,dailyRate:bulkCharge?+bulkCharge:0})});
      if(r.ok)ok++;
    }
    notify(`✅ Created ${ok}/${count} beds`);setSelWard(bulkWardId);setTab('beds');loadAll();
  };

  // ── Clinical Notes ────────────────────────────────────────────────────────
  const saveNote=async()=>{
    if(!selAdmission||!nf.noteType)return notify('⚠️ Note type required');
    setSaving(true);
    const r=await fetch(`${BASE}/ipd/notes`,{method:'POST',headers:hdr(),body:JSON.stringify({...nf,admissionId:selAdmission.id,patientId:selAdmission.patientId||selAdmission.patient_id})});
    if(r.ok){notify('✅ Note saved');setShowNoteForm(false);setNf({...DEF_NOTE});loadAdmissionData(selAdmission.id);}
    else notify('❌ Failed to save note');
    setSaving(false);
  };

  // ── Doctor Orders ─────────────────────────────────────────────────────────
  const saveOrder=async()=>{
    if(!selAdmission||!of.orderType)return;
    setSaving(true);
    const r=await fetch(`${BASE}/ipd/orders`,{method:'POST',headers:hdr(),body:JSON.stringify({...of,admissionId:selAdmission.id,patientId:selAdmission.patientId||selAdmission.patient_id,details:of.details})});
    if(r.ok){notify('✅ Order placed');setShowOrderForm(false);loadAdmissionData(selAdmission.id);}
    else notify('❌ Failed');
    setSaving(false);
  };

  const updateOrderStatus=async(id:string,status:string)=>{
    await fetch(`${BASE}/ipd/orders/${id}/status`,{method:'PUT',headers:hdr(),body:JSON.stringify({status})});
    notify('✅ Updated');if(selAdmission)loadAdmissionData(selAdmission.id);
  };

  // ── Transfer ──────────────────────────────────────────────────────────────
  const loadAvailBeds=async(wardId:string)=>{
    const r=await fetch(`${BASE}/ipd/beds?wardId=${wardId}&status=AVAILABLE`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]);
    setAvailBeds(Array.isArray(r)?r:r?.data||r?.beds||[]);
  };

  const saveTransfer=async()=>{
    if(!selAdmission||!tf.toWardId)return notify('⚠️ Select target ward');
    setSaving(true);
    const r=await fetch(`${BASE}/ipd/transfer`,{method:'POST',headers:hdr(),body:JSON.stringify({admissionId:selAdmission.id,patientId:selAdmission.patientId||selAdmission.patient_id,fromWardId:selAdmission.wardId||selAdmission.ward_id,fromBedId:selAdmission.bedId||selAdmission.bed_id,...tf})});
    if(r.ok){notify('✅ Patient transferred');setShowTransferForm(false);loadAll();loadAdmissionData(selAdmission.id);}
    else{const e=await r.json().catch(()=>({}));notify('❌ '+(e.message||'Transfer failed'));}
    setSaving(false);
  };

  // ── Discharge ─────────────────────────────────────────────────────────────
  const quickDischarge=async(admission:any)=>{
    if(!window.confirm(`Discharge ${admission.patientName||admission.patient_name||'patient'}?`))return;
    const r=await fetch(`${BASE}/ipd/admissions/${admission.id}/discharge`,{method:'PUT',headers:hdr(),body:JSON.stringify({dischargeDate:new Date().toISOString(),condition:'STABLE',dischargeType:'ROUTINE',dischargeNotes:'Discharged in stable condition'})});
    if(r.ok)notify('✅ Patient discharged');
    else notify('❌ Discharge failed');
    loadAll();
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const filtered=admissions.filter((a:any)=>{
    const matchStatus=!statusFilter||a.status===statusFilter;
    const s=search.toLowerCase();
    const matchSearch=!s||(a.patientName||a.patient_name||'').toLowerCase().includes(s)||(a.uhid||'').toLowerCase().includes(s)||(a.ipdNumber||a.ipd_number||'').toLowerCase().includes(s)||(a.wardName||a.ward_name||'').toLowerCase().includes(s);
    return matchStatus&&matchSearch;
  });

  const TABS=[['admissions','🏥 Admissions'],['beds','🛏️ Beds'],['wards','🏢 Wards'],['bulk','⚡ Bulk Add'],['rounds','📋 Rounds'],['notes','📝 Clinical Notes'],['orders','💊 Doctor Orders'],['transfers','↔️ Transfers']];

  return(
    <Layout title="IPD Management" actions={
      <div className="flex gap-2">
        {tab==='admissions'&&<button onClick={()=>nav('/ipd/admit')} className={BTN_PRI}>+ Admit Patient</button>}
        {tab==='wards'&&<button onClick={()=>{setEditWard(null);setWf({...DEF_WARD});setShowWardForm(true);}} className={BTN_PRI}>+ Add Ward</button>}
        {tab==='beds'&&selWard&&<button onClick={()=>{setEditBed(null);setBf({...DEF_BED,wardId:selWard});setShowBedForm(true);}} className={BTN_PRI}>+ Add Bed</button>}
        {tab==='bulk'&&<button onClick={bulkCreate} disabled={!bulkWardId} className={BTN_PRI}>⚡ Create Beds</button>}
        {(tab==='rounds'||tab==='notes')&&selAdmission&&<button onClick={()=>{setNf({...DEF_NOTE,admissionId:selAdmission.id});setShowNoteForm(true);}} className={BTN_PRI}>+ Add Note</button>}
        {tab==='orders'&&selAdmission&&<button onClick={()=>{loadMasters();setShowOrderForm(true);}} className={BTN_PRI}>+ Place Order</button>}
        {tab==='transfers'&&selAdmission&&<button onClick={()=>setShowTransferForm(true)} className={BTN_PRI}>↔️ Transfer Patient</button>}
        <button onClick={()=>nav('/bed-board')} className={BTN_SEC}>🗺️ Bed Board</button>
      </div>
    }>

      {/* Toast */}
      {msg&&<div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border ${msg.startsWith('✅')?'bg-green-50 text-green-800 border-green-200':msg.startsWith('⚠️')||msg.startsWith('⏳')?'bg-amber-50 text-amber-800 border-amber-200':'bg-red-50 text-red-800 border-red-200'}`}>{msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {[
          ['Total Admitted',stats.admitted||admissions.filter((a:any)=>a.status==='ADMITTED').length,'from-[#02357A] to-[#015CA6]'],
          ['Admitted Today',stats.today||0,'from-emerald-600 to-teal-600'],
          ['Pending Discharge',stats.pendingDischarge||0,'from-amber-500 to-orange-500'],
          ['ICU / Critical',stats.icu||0,'from-red-600 to-red-700'],
          ['Total Beds',stats.totalBeds||beds.length,'from-purple-600 to-indigo-600'],
          ['Available Beds',stats.availBeds||beds.filter((b:any)=>b.status==='AVAILABLE').length,'from-green-500 to-emerald-600'],
        ].map(([l,v,g])=>(
          <div key={l as string} className={`bg-gradient-to-br ${g} text-white rounded-2xl p-4 shadow-md`}>
            <p className="text-xs font-semibold opacity-80 uppercase tracking-wide truncate">{l}</p>
            <p className="text-3xl font-black mt-1">{v}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-thin">
        {TABS.map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)}
            className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${tab===t?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ══ ADMISSIONS ══ */}
      {tab==='admissions'&&(
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search patient, UHID, IPD number, ward..." className={`${INP} pl-10`}/>
            </div>
            <div className="flex gap-1 flex-wrap">
              {['','ADMITTED','PENDING_DISCHARGE','DISCHARGED','TRANSFERRED'].map(s=>(
                <button key={s} onClick={()=>setStatusFilter(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-all ${statusFilter===s?'bg-[#02357A] text-white border-[#02357A]':'border-slate-200 text-slate-600 hover:border-[#02357A]'}`}>
                  {s||'All'}
                </button>
              ))}
            </div>
          </div>

          {filtered.length===0?(
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center shadow-sm">
              <p className="text-5xl mb-3">🏥</p>
              <p className="text-lg font-bold text-slate-700">No admissions{search||statusFilter?' matching filters':' yet'}</p>
              <p className="text-slate-400 text-sm mt-1 mb-5">Click + Admit Patient to admit the first patient</p>
              <button onClick={()=>nav('/ipd/admit')} className={BTN_PRI}>+ Admit Patient</button>
            </div>
          ):(
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                <span className="font-bold text-slate-800">Active Admissions</span>
                <span className="text-xs text-slate-400">{filtered.length} record(s)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['IPD No.','Patient','Ward / Bed','Doctor','Admitted','Expected Discharge','Status','SHA','Actions'].map(h=>(
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((a:any)=>(
                      <tr key={a.id} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#02357A]">{a.ipdNumber||a.ipd_number||'—'}</td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-800">{a.patientName||a.patient_name||`${a.firstName||''} ${a.lastName||''}`.trim()||'—'}</p>
                          <p className="text-xs text-slate-400">{a.uhid||'—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-700">{a.wardName||a.ward_name||'—'}</p>
                          <p className="text-xs text-slate-400">Bed: {a.bedNumber||a.bed_number||'—'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{a.doctorName||a.doctor_name||'—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(a.admissionDate||a.admission_date)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(a.expectedDischargeDate||a.expected_discharge_date)||'—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${statusBadge(a.status)}`}>{a.status||'—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {/* SHA Status Badge + Action */}
                          {(() => {
                            const admUuid = a.admission_id||(a.id?.includes('-')?a.id:null);
                            const sha = admUuid ? shaStatuses[admUuid] : null;
                            const shaStatus = sha?.status || (sha ? 'INITIATED' : null);
                            // getBulkSHAStatus() returns { claimId, status, fund, scenario, requiresAction }
                            // per admission — there is no `.id` field on it. Reading sha?.id here always
                            // returned undefined, so the "→ Open Claim" link would 404 even once a claim
                            // legitimately existed.
                            const shaId = sha?.claimId;
                            const colorMap: Record<string,string> = {
                              DISCHARGED:'bg-emerald-100 text-emerald-700',
                              SUBMITTED:'bg-emerald-100 text-emerald-700',
                              PAID:'bg-green-100 text-green-700',
                              PREAUTH_FINALISED:'bg-blue-100 text-blue-700',
                              CLAIM_CREATED:'bg-teal-100 text-teal-700',
                              BILLING_ADDED:'bg-violet-100 text-violet-700',
                              PREVIEWED:'bg-indigo-100 text-indigo-700',
                              ELIGIBILITY_CONFIRMED:'bg-cyan-100 text-cyan-700',
                              INITIATED:'bg-amber-100 text-amber-700',
                              NOT_ELIGIBLE:'bg-red-100 text-red-700',
                              CREATING_PREAUTH:'bg-orange-100 text-orange-700',
                            };
                            const badgeColor = shaStatus ? (colorMap[shaStatus] || 'bg-slate-100 text-slate-600') : 'bg-slate-50 text-slate-400';
                            return (
                              <div className="flex flex-col gap-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold w-fit ${badgeColor}`}>
                                  {shaStatus || 'No SHA'}
                                </span>
                                {shaId ? (
                                  <button
                                    onClick={()=>{
                                      const pid=a.patientId||a.patient_id;
                                      const cr=a.sha_beneficiary_cr_id||a.shaBeneficiaryCrId||'';
                                      nav(`/sha/claims/${shaId}?from=ipd&admissionId=${a.id}&patientId=${pid||''}&crId=${cr}`);
                                    }}
                                    className="text-xs px-2 py-0.5 bg-[#039089] text-white rounded-lg font-semibold hover:bg-[#02357A] transition-all w-fit"
                                  >
                                    → Open Claim
                                  </button>
                                ) : isShaCoveredPatient(a) ? (
                                  <button
                                    onClick={()=>{
                                      const pid=a.patientId||a.patient_id;
                                      const cr=a.sha_beneficiary_cr_id||a.shaBeneficiaryCrId||'';
                                      const pname=`${a.patient_first_name||a.patientFirstName||a.patientName||a.patient_name||''} ${a.patient_last_name||a.patientLastName||''}`.trim();
                                      const qs=new URLSearchParams({from:'ipd',serviceType:'INPATIENT',admissionId:a.id,patientId:pid||'',patientName:pname});
                                      if(cr)qs.set('crId',cr);
                                      nav(`/sha/new-claim?${qs.toString()}`);
                                    }}
                                    className="text-xs px-2 py-0.5 bg-[#039089] text-white rounded-lg font-semibold hover:bg-[#02357A] transition-all w-fit"
                                  >
                                    + SHA Claim
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-slate-400">Cash / not SHA</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            <button onClick={()=>{setSelAdmission(a);setTab('rounds');}} className="px-2 py-1 rounded-lg text-xs bg-blue-50 text-[#02357A] hover:bg-blue-100 font-medium">Rounds</button>
                            <button onClick={()=>{setSelAdmission(a);setTab('notes');}} className="px-2 py-1 rounded-lg text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium">{t('common.notes','Notes')}</button>
                            <button onClick={()=>{setSelAdmission(a);setTab('orders');}} className="px-2 py-1 rounded-lg text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium">Orders</button>
                            {a.status==='ADMITTED'&&(
                              <>
                                <button onClick={()=>{setSelAdmission(a);setTab('transfers');}} className="px-2 py-1 rounded-lg text-xs bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium">Transfer</button>
                                <button onClick={()=>quickDischarge(a)} className="px-2 py-1 rounded-lg text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">Discharge</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ BEDS ══ */}
      {tab==='beds'&&(
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-48">
              <label className={LBL}>Select Ward</label>
              <select value={selWard} onChange={e=>setSelWard(e.target.value)} className={INP}>
                <option value="">All Wards ({beds.length} beds)</option>
                {wards.map((w:any)=><option key={w.id} value={w.id}>{w.name||w.wardName||w.wardCode} ({w.wardType})</option>)}
              </select>
            </div>
            {selWard&&(
              <div className="flex gap-3 text-sm pb-1">
                <span className="text-emerald-600 font-bold">✅ {beds.filter((b:any)=>b.status==='AVAILABLE').length} Available</span>
                <span className="text-red-600 font-bold">🛏️ {beds.filter((b:any)=>b.status==='OCCUPIED').length} Occupied</span>
                <span className="text-amber-600 font-bold">🔧 {beds.filter((b:any)=>!['AVAILABLE','OCCUPIED'].includes(b.status)).length} Other</span>
              </div>
            )}
            <button onClick={()=>{setBulkWardId(selWard||'');setTab('bulk');}} className={BTN_SEC}>⚡ Bulk Add</button>
          </div>

          {beds.length===0?(
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
              <p className="text-5xl mb-3">🛏️</p>
              <p className="text-lg font-bold text-slate-700">{wards.length===0?'No wards set up yet':'No beds in this ward'}</p>
              <p className="text-slate-400 text-sm mt-1 mb-5">{wards.length===0?'Go to Wards tab first':'Add individual beds or use Bulk Add'}</p>
              <div className="flex gap-3 justify-center">
                {wards.length===0
                  ?<button onClick={()=>setTab('wards')} className={BTN_PRI}>🏢 Go to Wards</button>
                  :<><button onClick={()=>{setEditBed(null);setBf({...DEF_BED,wardId:selWard});setShowBedForm(true);}} className={BTN_PRI}>+ Add Bed</button>
                    <button onClick={()=>{setBulkWardId(selWard);setTab('bulk');}} className={BTN_SEC}>⚡ Bulk Add</button></>
                }
              </div>
            </div>
          ):(
            <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
              {beds.map((b:any)=>{
                const num=b.bedNumber||b.bed_number||'?';
                const status=b.status||'AVAILABLE';
                const patient=b.currentPatientId||b.current_patient_id;
                return(
                  <div key={b.id} className={`bg-white rounded-2xl border-2 p-3 hover:shadow-md transition-all ${bedColor(status)}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="font-black text-sm">Bed {num}</p>
                      <span className={`text-xs font-bold`}>{status.slice(0,4)}</span>
                    </div>
                    <p className="text-xs opacity-70 mb-1">{b.bedType||b.bed_type||'GENERAL'}</p>
                    {b.roomNumber&&<p className="text-xs opacity-60 mb-1">Room {b.roomNumber}</p>}
                    {b.dailyRate>0&&<p className="text-xs font-semibold mb-2">{getCurrencySymbol()}{b.dailyRate}/day</p>}
                    <select value={status} onChange={e=>updateBedStatus(b.id,e.target.value)}
                      className="w-full bg-white/80 border border-current/20 rounded-lg px-2 py-1 text-xs mb-2 focus:outline-none">
                      {BED_STATUSES.map(s=><option key={s}>{s}</option>)}
                    </select>
                    <div className="flex gap-1">
                      <button onClick={()=>{setEditBed(b);setBf({bedNumber:num,wardId:b.wardId||b.ward_id||selWard,bedType:b.bedType||b.bed_type||'GENERAL',status,floor:b.floor||'',roomNumber:b.roomNumber||b.room_number||'',dailyRate:String(b.dailyRate||b.daily_rate||''),notes:b.notes||'',isActive:b.isActive!==false});setShowBedForm(true);}}
                        className="flex-1 py-1 rounded-lg text-xs bg-white/60 hover:bg-white/80 font-medium">✏️</button>
                      <button onClick={()=>deleteBed(b.id)} className="px-2 py-1 rounded-lg text-xs bg-white/60 hover:bg-red-100 text-red-500">🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ WARDS ══ */}
      {tab==='wards'&&(
        <>
          {wards.length===0?(
            <div className="bg-white rounded-2xl border p-16 text-center shadow-sm">
              <p className="text-5xl mb-3">🏢</p>
              <p className="text-lg font-bold text-slate-700">No wards created yet</p>
              <p className="text-slate-400 text-sm mb-5">Add wards to enable bed management</p>
              <button onClick={()=>{setEditWard(null);setWf({...DEF_WARD});setShowWardForm(true);}} className={BTN_PRI}>+ Add First Ward</button>
            </div>
          ):(
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wards.map((w:any)=>{
                const wardBeds=beds.filter((b:any)=>(b.wardId||b.ward_id)===w.id);
                const avail=wardBeds.filter((b:any)=>b.status==='AVAILABLE').length;
                const occ=wardBeds.filter((b:any)=>b.status==='OCCUPIED').length;
                return(
                  <div key={w.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-black text-slate-900 text-base">{w.name||w.wardName||w.wardCode}</p>
                        <p className="text-xs text-slate-400 font-mono mt-0.5">{w.wardCode} · Floor {w.floor||'1'}</p>
                      </div>
                      <span className="px-2.5 py-1 bg-blue-50 text-[#02357A] rounded-xl text-xs font-bold border border-blue-100">{w.wardType}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4 bg-slate-50 rounded-xl p-3 text-center">
                      <div><p className="text-lg font-black text-slate-800">{w.totalBeds||0}</p><p className="text-xs text-slate-400">Capacity</p></div>
                      <div><p className="text-lg font-black text-emerald-600">{avail}</p><p className="text-xs text-slate-400">Available</p></div>
                      <div><p className="text-lg font-black text-red-600">{occ}</p><p className="text-xs text-slate-400">Occupied</p></div>
                    </div>
                    {/* Occupancy bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>Occupancy</span>
                        <span>{wardBeds.length>0?Math.round((occ/wardBeds.length)*100):0}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-2 bg-[#02357A] rounded-full transition-all" style={{width:`${wardBeds.length>0?Math.round((occ/wardBeds.length)*100):0}%`}}/>
                      </div>
                    </div>
                    {w.dailyRate>0&&<p className="text-xs text-emerald-600 font-bold mb-3">{getCurrencySymbol()}{w.dailyRate.toLocaleString('en-KE')}/day</p>}
                    <div className="flex gap-2">
                      <button onClick={()=>{setSelWard(w.id);setTab('beds');}} className="flex-1 py-2 rounded-xl text-xs font-bold bg-blue-50 text-[#02357A] hover:bg-blue-100 border border-blue-100">🛏️ Beds</button>
                      <button onClick={()=>{setBulkWardId(w.id);setTab('bulk');}} className="px-3 py-2 rounded-xl text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-100">⚡</button>
                      <button onClick={()=>{setEditWard(w);setWf({wardCode:w.wardCode||'',name:w.name||w.wardName||'',wardType:w.wardType||'GENERAL',totalBeds:String(w.totalBeds||0),floor:String(w.floor||''),description:w.description||'',isActive:w.isActive!==false,dailyRate:String(w.dailyRate||'')});setShowWardForm(true);}}
                        className="px-3 py-2 rounded-xl text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200">✏️</button>
                      <button onClick={()=>deleteWard(w.id)} className="px-3 py-2 rounded-xl text-xs bg-red-50 text-red-500 hover:bg-red-100 border border-red-100">🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ══ BULK ADD ══ */}
      {tab==='bulk'&&(
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="font-black text-xl text-slate-800 mb-1">⚡ Bulk Add Beds</h2>
            <p className="text-sm text-slate-400 mb-5">Create multiple beds for a ward in one click</p>
            <div className="space-y-4">
              <Field label="Ward" required>
                <select value={bulkWardId} onChange={e=>setBulkWardId(e.target.value)} className={INP}>
                  <option value="">Select Ward</option>
                  {wards.map((w:any)=><option key={w.id} value={w.id}>{w.name||w.wardCode} ({w.wardType})</option>)}
                </select>
              </Field>
              <Field label="Bed Number Prefix">
                <input value={bulkPrefix} onChange={e=>setBulkPrefix(e.target.value)} placeholder="Auto from ward code (e.g. GEN, ICU)" className={INP}/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start From"><input type="number" value={bulkStart} onChange={e=>setBulkStart(e.target.value)} placeholder="1" className={INP}/></Field>
                <Field label="Number of Beds" required><input type="number" value={bulkCount} onChange={e=>setBulkCount(e.target.value)} placeholder="10" className={INP}/></Field>
              </div>
              <Field label="Bed Type">
                <select value={bulkType} onChange={e=>setBulkType(e.target.value)} className={INP}>
                  {BED_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Daily Rate ({getCurrencySymbol()})">
                <input type="number" value={bulkCharge} onChange={e=>setBulkCharge(e.target.value)} placeholder="800" className={INP}/>
              </Field>
              {bulkWardId&&bulkCount&&(
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm text-[#02357A] font-medium">
                  Will create <strong>{bulkCount} beds</strong> · Type: {bulkType} · Status: AVAILABLE{bulkCharge?` · ${getCurrencySymbol()}${bulkCharge}/day`:''}
                </div>
              )}
              <button onClick={bulkCreate} disabled={!bulkWardId||!bulkCount} className={`${BTN_PRI} w-full py-3 text-base`}>
                ⚡ Create {bulkCount||'N'} Beds Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ ROUNDS / NOTES / ORDERS / TRANSFERS (need patient selected) ══ */}
      {(['rounds','notes','orders','transfers'] as const).includes(tab as any)&&(
        <div className="space-y-4">
          {/* Patient selector */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <label className={LBL}>Select Admitted Patient</label>
            <select value={selAdmission?.id||''} onChange={e=>{const a=admissions.find((x:any)=>x.id===e.target.value);setSelAdmission(a||null);}} className={INP}>
              <option value="">— Select patient —</option>
              {admissions.filter((a:any)=>a.status==='ADMITTED').map((a:any)=>(
                <option key={a.id} value={a.id}>{a.patientName||a.patient_name||a.uhid} · {a.wardName||a.ward_name||'—'} Bed {a.bedNumber||a.bed_number||'—'} · IPD {a.ipdNumber||a.ipd_number||'—'}</option>
              ))}
            </select>
            {selAdmission&&(
              <div className="mt-3 flex flex-wrap gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100 text-xs">
                <span className="font-bold text-[#02357A]">IPD: {selAdmission.ipdNumber||selAdmission.ipd_number}</span>
                <span className="text-slate-600">Ward: {selAdmission.wardName||selAdmission.ward_name}</span>
                <span className="text-slate-600">Bed: {selAdmission.bedNumber||selAdmission.bed_number}</span>
                <span className="text-slate-600">Admitted: {fmtDate(selAdmission.admissionDate||selAdmission.admission_date)}</span>
                <span className={`px-2 py-0.5 rounded-full font-bold ${statusBadge(selAdmission.status)}`}>{selAdmission.status}</span>
              </div>
            )}
          </div>

          {!selAdmission?(
            <div className="bg-white rounded-2xl border p-12 text-center shadow-sm">
              <p className="text-4xl mb-3">👤</p>
              <p className="font-semibold text-slate-600">Select an admitted patient above</p>
            </div>
          ):(
            <>
              {/* ── ROUNDS ── */}
              {tab==='rounds'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">📋 Doctor Rounds</h3>
                    <span className="text-xs text-slate-400">{rounds.length} round(s)</span>
                  </div>
                  {rounds.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">📋</p><p className="text-slate-400 text-sm">No rounds recorded yet</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {rounds.map((r:any)=>(
                        <div key={r.id} className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-slate-500">{fmtDateTime(r.roundDate||r.round_date)}</span>
                            {r.doctorName&&<span className="text-xs text-slate-400">· Dr. {r.doctorName}</span>}
                          </div>
                          {r.clinicalFindings&&<p className="text-sm text-slate-700"><strong>Findings:</strong> {r.clinicalFindings}</p>}
                          {r.assessment&&<p className="text-sm text-slate-700 mt-1"><strong>Assessment:</strong> {r.assessment}</p>}
                          {r.plan&&<p className="text-sm text-slate-700 mt-1"><strong>Plan:</strong> {r.plan}</p>}
                          {r.treatmentChanges&&<p className="text-sm text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded-lg">🔄 Changes: {r.treatmentChanges}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── CLINICAL NOTES ── */}
              {tab==='notes'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">📝 Clinical Notes</h3>
                    <span className="text-xs text-slate-400">{notes.length} note(s)</span>
                  </div>
                  {notes.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">📝</p><p className="text-slate-400 text-sm">No notes recorded yet</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {notes.map((n:any)=>(
                        <div key={n.id} className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold bg-blue-50 text-[#02357A] px-2 py-0.5 rounded-full">{n.noteType||'PROGRESS'}</span>
                            {n.shift&&<span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{n.shift}</span>}
                            <span className="text-xs text-slate-400 ml-auto">{fmtDateTime(n.createdAt||n.created_at)}</span>
                          </div>
                          {(n.subjective||n.objective||n.assessment||n.plan)?(
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {n.subjective&&<div className="bg-slate-50 rounded-xl p-2"><p className="font-bold text-slate-400 uppercase mb-1">Subjective</p><p className="text-slate-700">{n.subjective}</p></div>}
                              {n.objective&&<div className="bg-slate-50 rounded-xl p-2"><p className="font-bold text-slate-400 uppercase mb-1">Objective</p><p className="text-slate-700">{n.objective}</p></div>}
                              {n.assessment&&<div className="bg-blue-50 rounded-xl p-2"><p className="font-bold text-blue-400 uppercase mb-1">Assessment</p><p className="text-slate-700">{n.assessment}</p></div>}
                              {n.plan&&<div className="bg-emerald-50 rounded-xl p-2"><p className="font-bold text-emerald-400 uppercase mb-1">Plan</p><p className="text-slate-700">{n.plan}</p></div>}
                            </div>
                          ):<p className="text-sm text-slate-700">{n.noteText}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── DOCTOR ORDERS ── */}
              {tab==='orders'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">💊 Doctor Orders</h3>
                    <span className="text-xs text-slate-400">{orders.length} order(s)</span>
                  </div>
                  {orders.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">💊</p><p className="text-slate-400 text-sm">No orders placed yet</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {orders.map((o:any)=>{
                        const det=typeof o.details==='string'?JSON.parse(o.details||'{}'):o.details||{};
                        return(
                          <div key={o.id} className="p-4 flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-bold text-slate-800 text-sm">{o.orderType}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${o.priority==='STAT'?'bg-red-100 text-red-700':o.priority==='URGENT'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600'}`}>{o.priority}</span>
                                <span className="font-mono text-xs text-slate-400">{o.orderNumber||o.order_number}</span>
                              </div>
                              {det.itemName&&<p className="text-sm text-slate-700 font-medium">{det.itemName}</p>}
                              {det.dosage&&<p className="text-xs text-slate-500">{det.dosage}{det.route?` · ${det.route}`:''}{det.frequency?` · ${det.frequency}`:''}{det.duration?` · ${det.duration}`:''}</p>}
                              {o.notes&&<p className="text-xs text-slate-400 mt-1 italic">{o.notes}</p>}
                              <p className="text-xs text-slate-400 mt-1">{fmtDateTime(o.createdAt||o.created_at)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${o.status==='COMPLETED'?'bg-emerald-100 text-emerald-700':o.status==='ACTIVE'?'bg-blue-100 text-[#02357A]':o.status==='CANCELLED'?'bg-red-100 text-red-700':'bg-slate-100 text-slate-500'}`}>{o.status}</span>
                              {o.status==='ACTIVE'&&(
                                <div className="flex gap-1">
                                  <button onClick={()=>updateOrderStatus(o.id,'COMPLETED')} className="text-xs px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Done</button>
                                  <button onClick={()=>updateOrderStatus(o.id,'CANCELLED')} className="text-xs px-2 py-0.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">{t('common.cancel','Cancel')}</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── TRANSFERS ── */}
              {tab==='transfers'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">↔️ Transfer History</h3>
                    <span className="text-xs text-slate-400">{transfers.length} transfer(s)</span>
                  </div>
                  {transfers.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">↔️</p><p className="text-slate-400 text-sm">No transfers recorded</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {transfers.map((t:any)=>(
                        <div key={t.id} className="p-4">
                          <div className="flex items-center gap-3 text-sm">
                            <div className="text-center bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                              <p className="font-bold text-red-700">{t.fromWardName||t.from_ward_name||'—'}</p>
                              <p className="text-xs text-red-500">Bed {t.fromBedNumber||t.from_bed_number||'—'}</p>
                            </div>
                            <span className="text-2xl text-slate-400">→</span>
                            <div className="text-center bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                              <p className="font-bold text-emerald-700">{t.toWardName||t.to_ward_name||'—'}</p>
                              <p className="text-xs text-emerald-600">Bed {t.toBedNumber||t.to_bed_number||'—'}</p>
                            </div>
                          </div>
                          {t.transferReason&&<p className="text-xs text-slate-500 mt-2">Reason: {t.transferReason||t.transfer_reason}</p>}
                          <p className="text-xs text-slate-400 mt-1">{fmtDateTime(t.transferDate||t.transfer_date)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ WARD MODAL ══ */}
      <Modal open={showWardForm} onClose={()=>setShowWardForm(false)} title={editWard?'Edit Ward':'Add New Ward'}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Ward Code" required><input value={wf.wardCode} onChange={e=>setWf(x=>({...x,wardCode:e.target.value.toUpperCase()}))} placeholder="GEN-M, ICU-1" className={INP}/></Field>
          <Field label="Ward Name" required><input value={wf.name} onChange={e=>setWf(x=>({...x,name:e.target.value}))} placeholder="General Medicine Male" className={INP}/></Field>
          <Field label="Ward Type" required>
            <select value={wf.wardType} onChange={e=>setWf(x=>({...x,wardType:e.target.value}))} className={INP}>
              {WARD_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Floor"><input value={wf.floor} onChange={e=>setWf(x=>({...x,floor:e.target.value}))} placeholder="Ground, 1, 2" className={INP}/></Field>
          <Field label="Total Bed Capacity"><input type="number" value={wf.totalBeds} onChange={e=>setWf(x=>({...x,totalBeds:e.target.value}))} placeholder="20" className={INP}/></Field>
          <Field label="Daily Rate ({getCurrencySymbol()})"><input type="number" value={wf.dailyRate} onChange={e=>setWf(x=>({...x,dailyRate:e.target.value}))} placeholder="800" className={INP}/></Field>
          <Field label="Description" col2><input value={wf.description} onChange={e=>setWf(x=>({...x,description:e.target.value}))} placeholder="Optional description" className={INP}/></Field>
          <div className="col-span-2 flex items-center gap-3">
            <input type="checkbox" checked={wf.isActive} onChange={e=>setWf(x=>({...x,isActive:e.target.checked}))} className="w-4 h-4 accent-[#02357A]"/>
            <label className="text-sm font-semibold text-slate-700">Active Ward</label>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={()=>setShowWardForm(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
          <button onClick={saveWard} disabled={saving} className={`${BTN_PRI} flex-1`}>{saving?'Saving…':editWard?'Update Ward':'Create Ward'}</button>
        </div>
      </Modal>

      {/* ══ BED MODAL ══ */}
      <Modal open={showBedForm} onClose={()=>setShowBedForm(false)} title={editBed?'Edit Bed':'Add New Bed'}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bed Number" required><input value={bf.bedNumber} onChange={e=>setBf(x=>({...x,bedNumber:e.target.value}))} placeholder="GEN-01" className={INP}/></Field>
          <Field label="Bed Type">
            <select value={bf.bedType} onChange={e=>setBf(x=>({...x,bedType:e.target.value}))} className={INP}>
              {BED_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Ward" required>
            <select value={bf.wardId} onChange={e=>setBf(x=>({...x,wardId:e.target.value}))} className={INP}>
              <option value="">Select Ward</option>
              {wards.map((w:any)=><option key={w.id} value={w.id}>{w.name||w.wardCode} ({w.wardType})</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={bf.status} onChange={e=>setBf(x=>({...x,status:e.target.value}))} className={INP}>
              {BED_STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Floor"><input value={bf.floor} onChange={e=>setBf(x=>({...x,floor:e.target.value}))} placeholder="1" className={INP}/></Field>
          <Field label="Room Number"><input value={bf.roomNumber} onChange={e=>setBf(x=>({...x,roomNumber:e.target.value}))} placeholder="101" className={INP}/></Field>
          <Field label="Daily Rate ({getCurrencySymbol()})"><input type="number" value={bf.dailyRate} onChange={e=>setBf(x=>({...x,dailyRate:e.target.value}))} placeholder="800" className={INP}/></Field>
          <Field label="Notes"><input value={bf.notes} onChange={e=>setBf(x=>({...x,notes:e.target.value}))} placeholder="AC, TV, Attached Bath" className={INP}/></Field>
          <div className="col-span-2 flex items-center gap-3">
            <input type="checkbox" checked={bf.isActive} onChange={e=>setBf(x=>({...x,isActive:e.target.checked}))} className="w-4 h-4 accent-[#02357A]"/>
            <label className="text-sm font-semibold text-slate-700">Active Bed</label>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={()=>setShowBedForm(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
          <button onClick={saveBed} disabled={saving} className={`${BTN_PRI} flex-1`}>{saving?'Saving…':editBed?'Update Bed':'Create Bed'}</button>
        </div>
      </Modal>

      {/* ══ CLINICAL NOTE MODAL ══ */}
      <Modal open={showNoteForm} onClose={()=>setShowNoteForm(false)} title="📝 Add Clinical Note" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Note Type" required>
              <select value={nf.noteType} onChange={e=>setNf(x=>({...x,noteType:e.target.value}))} className={INP}>
                {NOTE_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Shift">
              <select value={nf.shift} onChange={e=>setNf(x=>({...x,shift:e.target.value}))} className={INP}>
                {['MORNING','AFTERNOON','EVENING','NIGHT'].map(s=><option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Subjective (S)"><textarea value={nf.subjective} onChange={e=>setNf(x=>({...x,subjective:e.target.value}))} rows={3} placeholder="Patient complaints, symptoms..." className={`${INP} resize-y min-h-[80px]`}/></Field>
            <Field label="Objective (O)"><textarea value={nf.objective} onChange={e=>setNf(x=>({...x,objective:e.target.value}))} rows={3} placeholder="Vital signs, examination findings..." className={`${INP} resize-y min-h-[80px]`}/></Field>
            <Field label="Assessment (A)"><textarea value={nf.assessment} onChange={e=>setNf(x=>({...x,assessment:e.target.value}))} rows={3} placeholder="Clinical assessment, diagnosis..." className={`${INP} resize-y min-h-[80px]`}/></Field>
            <Field label="Plan (P)"><textarea value={nf.plan} onChange={e=>setNf(x=>({...x,plan:e.target.value}))} rows={3} placeholder="Treatment plan, orders, goals..." className={`${INP} resize-y min-h-[80px]`}/></Field>
          </div>
          <Field label="Free Text Note"><textarea value={nf.noteText} onChange={e=>setNf(x=>({...x,noteText:e.target.value}))} rows={3} placeholder="Additional observations..." className={`${INP} resize-y min-h-[80px]`}/></Field>
          <div className="flex gap-3">
            <button onClick={()=>setShowNoteForm(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={saveNote} disabled={saving} className={`${BTN_PRI} flex-1`}>{saving?'Saving…':'💾 Save Note'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ DOCTOR ORDER MODAL ══ */}
      <Modal open={showOrderForm} onClose={()=>{setShowOrderForm(false);setDrugSearch('');setOrderSearch('');}} title="💊 Place Doctor Order" size="lg">
        <div className="space-y-4">
          {/* Order Type Tabs */}
          <div className="flex gap-2 flex-wrap">
            {[['MEDICATION','💊'],['LAB','🔬'],['RADIOLOGY','🩻'],['PROCEDURE','🏥'],['DIET','🍽️'],['NURSING','👩‍⚕️'],['BLOOD','🩸']].map(([t,icon])=>(
              <button key={t} type="button" onClick={()=>{setOf(x=>({...x,orderType:t,details:{itemName:'',dosage:'',route:'Oral',frequency:'BD',duration:'5 Days'}}));setDrugSearch('');setOrderSearch('');}}
                className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all ${of.orderType===t?'bg-[#02357A] text-white border-[#02357A] shadow-md':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A]'}`}>
                {icon} {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority">
              <select value={of.priority} onChange={e=>setOf(x=>({...x,priority:e.target.value}))} className={INP}>
                {['ROUTINE','URGENT','STAT'].map(p=><option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Scheduled At">
              <input type="datetime-local" value={of.details.scheduledAt||''} onChange={e=>setOf(x=>({...x,details:{...x.details,scheduledAt:e.target.value}}))} className={INP}/>
            </Field>
          </div>

          {/* ── MEDICATION ── */}
          {of.orderType==='MEDICATION'&&(
            <div className="space-y-3 p-4 bg-blue-50 rounded-2xl border border-blue-100">
              <Field label="Search Drug from Formulary" required>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">💊</span>
                  <input value={drugSearch} onChange={e=>{setDrugSearch(e.target.value);if(!e.target.value)setOf(x=>({...x,details:{...x.details,itemName:'',dosage:''}}));}}
                    placeholder="Type drug name..." className={`${INP} pl-10`}/>
                </div>
                {/* Drug results */}
                {drugSearch.length>=2&&(
                  <div className="mt-1 bg-white border-2 border-slate-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                    {masterDrugs.filter((d:any)=>(d.name||d.drugName||'').toLowerCase().includes(drugSearch.toLowerCase())||(d.genericName||d.generic_name||'').toLowerCase().includes(drugSearch.toLowerCase())).slice(0,10).map((d:any)=>(
                      <div key={d.id} onClick={()=>{
                        setDrugSearch(d.name||d.drugName||'');
                        setOf(x=>({...x,details:{...x.details,itemName:d.name||d.drugName,dosage:d.strength||x.details.dosage,route:d.dosageForm==='Injection'?'IV':d.dosageForm==='Inhaler'?'Inhaled':'Oral'}}));
                      }} className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-800">{d.name||d.drugName}</p>
                          <p className="text-xs text-slate-400">{d.genericName||d.generic_name} · {d.dosageForm||d.dosage_form} · {d.strength}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${(d.totalStock||d.total_stock||0)>0?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>
                          Stock: {d.totalStock||d.total_stock||0}
                        </span>
                      </div>
                    ))}
                    {masterDrugs.filter((d:any)=>(d.name||d.drugName||'').toLowerCase().includes(drugSearch.toLowerCase())).length===0&&(
                      <div className="p-3 text-center text-sm text-slate-400">No drugs found — you can type a custom name</div>
                    )}
                  </div>
                )}
                {/* Manual entry fallback */}
                {!drugSearch&&<p className="text-xs text-slate-400 mt-1">Or type drug name manually below</p>}
              </Field>
              {(!drugSearch||(of.details.itemName&&drugSearch!==of.details.itemName))&&(
                <Field label="Drug Name (Manual)">
                  <input value={of.details.itemName||''} onChange={e=>setOf(x=>({...x,details:{...x.details,itemName:e.target.value}}))} placeholder="e.g. Paracetamol 500mg" className={INP}/>
                </Field>
              )}
              {of.details.itemName&&<p className="text-xs text-emerald-600 font-bold">✓ {of.details.itemName}</p>}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dosage">
                  <input value={of.details.dosage||''} onChange={e=>setOf(x=>({...x,details:{...x.details,dosage:e.target.value}}))} placeholder="500mg, 1 tab, 5ml" className={INP}/>
                </Field>
                <Field label="Route">
                  <select value={of.details.route||'Oral'} onChange={e=>setOf(x=>({...x,details:{...x.details,route:e.target.value}}))} className={INP}>
                    {['Oral','IV (Slow)','IV (Push)','IV Infusion','IM','SC','SL','Topical','Inhaled','NGT','PR','Eye Drops','Ear Drops'].map(r=><option key={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Frequency">
                  <select value={of.details.frequency||'BD'} onChange={e=>setOf(x=>({...x,details:{...x.details,frequency:e.target.value}}))} className={INP}>
                    {['STAT (Once now)','OD (Once daily)','BD (Twice daily)','TDS (Thrice daily)','QDS (Four times)','Q4H (Every 4h)','Q6H (Every 6h)','Q8H (Every 8h)','Q12H (Every 12h)','PRN (As needed)','CONTINUOUS','HS (At bedtime)','AC (Before meals)','PC (After meals)'].map(f=><option key={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Duration">
                  <input value={of.details.duration||''} onChange={e=>setOf(x=>({...x,details:{...x.details,duration:e.target.value}}))} placeholder="5 Days, 1 Week, Till discharge" className={INP}/>
                </Field>
              </div>
            </div>
          )}

          {/* ── LAB ── */}
          {of.orderType==='LAB'&&(
            <div className="space-y-3 p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
              <Field label="Search Lab Test" required>
                <input value={orderSearch} onChange={e=>setOrderSearch(e.target.value)} placeholder="Type test name e.g. CBC, LFT, KFT, HBA1C..." className={INP}/>
              </Field>
              {orderSearch.length>=2&&(
                <div className="bg-white border-2 border-slate-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                  {masterLab.filter((t:any)=>(t.testName||t.test_name||t.name||'').toLowerCase().includes(orderSearch.toLowerCase())).slice(0,10).map((t:any)=>{
                    const name=t.testName||t.test_name||t.name;
                    return(
                      <div key={t.id} onClick={()=>{setOf(x=>({...x,details:{...x.details,itemName:name,testCode:t.testCode||t.test_code||''}}));setOrderSearch(name);}}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 cursor-pointer border-b border-slate-100 last:border-0">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-800">{name}</p>
                          <p className="text-xs text-slate-400">{t.testCode||t.test_code} · {t.testCategory||t.category||'Lab'}</p>
                        </div>
                        {t.price&&<span className="text-xs text-emerald-700 font-bold">{getCurrencySymbol()}{t.price}</span>}
                      </div>
                    );
                  })}
                  {masterLab.filter((t:any)=>(t.testName||t.test_name||t.name||'').toLowerCase().includes(orderSearch.toLowerCase())).length===0&&(
                    <div className="p-3 text-center text-sm text-slate-400">No tests found — type custom test name</div>
                  )}
                </div>
              )}
              {of.details.itemName&&<p className="text-xs text-emerald-600 font-bold">✓ {of.details.itemName}</p>}
              <Field label="Urgency / Special Instructions">
                <input value={of.details.urgency||''} onChange={e=>setOf(x=>({...x,details:{...x.details,urgency:e.target.value}}))} placeholder="Fasting, early morning, urgent..." className={INP}/>
              </Field>
            </div>
          )}

          {/* ── RADIOLOGY ── */}
          {of.orderType==='RADIOLOGY'&&(
            <div className="space-y-3 p-4 bg-purple-50 rounded-2xl border border-purple-100">
              <Field label="Search Imaging Study" required>
                <input value={orderSearch} onChange={e=>setOrderSearch(e.target.value)} placeholder="Type study e.g. X-Ray, USG, CT, MRI..." className={INP}/>
              </Field>
              {orderSearch.length>=2&&(
                <div className="bg-white border-2 border-slate-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                  {masterRad.filter((s:any)=>(s.serviceName||s.service_name||s.name||'').toLowerCase().includes(orderSearch.toLowerCase())).slice(0,10).map((s:any)=>{
                    const name=s.serviceName||s.service_name||s.name;
                    return(
                      <div key={s.id} onClick={()=>{setOf(x=>({...x,details:{...x.details,itemName:name,modality:s.modality||''}}));setOrderSearch(name);}}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 cursor-pointer border-b border-slate-100 last:border-0">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-800">{name}</p>
                          <p className="text-xs text-slate-400">{s.modality} · {s.bodyPart||s.body_part||''}</p>
                        </div>
                        {(s.basePrice||s.base_price)&&<span className="text-xs text-purple-700 font-bold">{getCurrencySymbol()}{s.basePrice||s.base_price}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {of.details.itemName&&<p className="text-xs text-purple-700 font-bold">✓ {of.details.itemName}</p>}
              <Field label="Clinical Indication">
                <input value={of.details.indication||''} onChange={e=>setOf(x=>({...x,details:{...x.details,indication:e.target.value}}))} placeholder="Reason for imaging..." className={INP}/>
              </Field>
              <Field label="Contrast Required">
                <select value={of.details.contrast||'NO'} onChange={e=>setOf(x=>({...x,details:{...x.details,contrast:e.target.value}}))} className={INP}>
                  {['NO','YES - IV','YES - ORAL','YES - BOTH'].map(c=><option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          )}

          {/* ── PROCEDURE ── */}
          {of.orderType==='PROCEDURE'&&(
            <div className="space-y-3 p-4 bg-orange-50 rounded-2xl border border-orange-100">
              <Field label="Search Procedure" required>
                <input value={orderSearch} onChange={e=>setOrderSearch(e.target.value)} placeholder="Type procedure e.g. Dressing, ECG, IV Cannula..." className={INP}/>
              </Field>
              {orderSearch.length>=2&&(
                <div className="bg-white border-2 border-slate-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                  {masterProc.filter((p:any)=>(p.procedureName||p.procedure_name||p.name||'').toLowerCase().includes(orderSearch.toLowerCase())).slice(0,10).map((p:any)=>{
                    const name=p.procedureName||p.procedure_name||p.name;
                    return(
                      <div key={p.id} onClick={()=>{setOf(x=>({...x,details:{...x.details,itemName:name,procedureCode:p.procedureCode||p.procedure_code||''}}));setOrderSearch(name);}}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-0">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-800">{name}</p>
                          <p className="text-xs text-slate-400">{p.category} · {p.procedureCode||p.procedure_code}</p>
                        </div>
                        {p.cost&&<span className="text-xs text-orange-700 font-bold">{getCurrencySymbol()}{p.cost}</span>}
                      </div>
                    );
                  })}
                  {masterProc.filter((p:any)=>(p.procedureName||p.procedure_name||p.name||'').toLowerCase().includes(orderSearch.toLowerCase())).length===0&&(
                    <div className="p-3 text-center text-sm text-slate-400">No procedures found — type custom name</div>
                  )}
                </div>
              )}
              {of.details.itemName&&<p className="text-xs text-orange-700 font-bold">✓ {of.details.itemName}</p>}
            </div>
          )}

          {/* ── DIET ── */}
          {of.orderType==='DIET'&&(
            <div className="space-y-3 p-4 bg-yellow-50 rounded-2xl border border-yellow-100">
              <Field label="Diet Type" required>
                <select value={of.details.itemName||''} onChange={e=>setOf(x=>({...x,details:{...x.details,itemName:e.target.value}}))} className={INP}>
                  <option value="">Select Diet Type</option>
                  {masterDiet.length>0
                    ?masterDiet.map((d:any)=><option key={d.id} value={d.name||d.dietName||d.diet_name}>{d.name||d.dietName||d.diet_name}</option>)
                    :['REGULAR','NPO (Nil by Mouth)','LIQUID','SEMI-LIQUID','SOFT','DIABETIC','LOW SALT','LOW FAT','HIGH PROTEIN','RENAL','CARDIAC','VEGETARIAN','VEGAN'].map(d=><option key={d}>{d}</option>)
                  }
                </select>
              </Field>
              <Field label="Meal Frequency">
                <select value={of.details.frequency||'3 Meals'} onChange={e=>setOf(x=>({...x,details:{...x.details,frequency:e.target.value}}))} className={INP}>
                  {['3 Meals (Regular)','6 Small Meals','2 Meals','Continuous Feeding (NGT)','On Demand'].map(f=><option key={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Dietary Restrictions">
                <input value={of.details.restrictions||''} onChange={e=>setOf(x=>({...x,details:{...x.details,restrictions:e.target.value}}))} placeholder="e.g. No sugar, No salt, No dairy..." className={INP}/>
              </Field>
            </div>
          )}

          {/* ── NURSING ── */}
          {of.orderType==='NURSING'&&(
            <div className="space-y-3 p-4 bg-teal-50 rounded-2xl border border-teal-100">
              <Field label="Nursing Order" required>
                <select value={of.details.itemName||''} onChange={e=>setOf(x=>({...x,details:{...x.details,itemName:e.target.value}}))} className={INP}>
                  <option value="">Select Nursing Order</option>
                  {NURSING_ORDERS.map(n=><option key={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Frequency / When">
                <select value={of.details.frequency||''} onChange={e=>setOf(x=>({...x,details:{...x.details,frequency:e.target.value}}))} className={INP}>
                  <option value="">{t('common.select','Select')}</option>
                  {['Once','Hourly','Q2H','Q4H','Q6H','Q8H','BD','TDS','QDS','Continuous','Per Shift','Daily','PRN'].map(f=><option key={f}>{f}</option>)}
                </select>
              </Field>
              {of.details.itemName&&<p className="text-xs text-teal-700 font-bold">✓ {of.details.itemName}</p>}
            </div>
          )}

          {/* ── BLOOD ── */}
          {of.orderType==='BLOOD'&&(
            <div className="space-y-3 p-4 bg-red-50 rounded-2xl border border-red-100">
              <Field label="Blood Component" required>
                <select value={of.details.itemName||''} onChange={e=>setOf(x=>({...x,details:{...x.details,itemName:e.target.value}}))} className={INP}>
                  <option value="">Select Component</option>
                  {['Whole Blood','Packed Red Cells (PRBC)','Platelet Concentrate (PC)','Fresh Frozen Plasma (FFP)','Cryoprecipitate','Single Donor Platelets (SDP)','Granulocyte Concentrate','Albumin 5%','Albumin 25%'].map(c=><option key={c}>{c}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Units / Volume">
                  <input value={of.details.units||''} onChange={e=>setOf(x=>({...x,details:{...x.details,units:e.target.value}}))} placeholder="2 units, 500ml" className={INP}/>
                </Field>
                <Field label="Blood Group Required">
                  <select value={of.details.bloodGroup||''} onChange={e=>setOf(x=>({...x,details:{...x.details,bloodGroup:e.target.value}}))} className={INP}>
                    <option value="">Any compatible</option>
                    {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(g=><option key={g}>{g}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Clinical Indication" required>
                <input value={of.details.indication||''} onChange={e=>setOf(x=>({...x,details:{...x.details,indication:e.target.value}}))} placeholder="e.g. Post-op anaemia, Hb < 7 g/dL, GI bleed" className={INP}/>
              </Field>
              <div className="bg-red-100 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-medium">
                ⚠️ Blood transfusion requires signed consent and pre-transfusion testing (blood grouping + cross-match)
              </div>
            </div>
          )}

          <Field label="Notes / Instructions">
            <textarea value={of.notes} onChange={e=>setOf(x=>({...x,notes:e.target.value}))} rows={2} placeholder="Special instructions, precautions..." className={`${INP} resize-y min-h-[60px]`}/>
          </Field>
          <div className="flex gap-3">
            <button onClick={()=>{setShowOrderForm(false);setDrugSearch('');setOrderSearch('');}} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={saveOrder} disabled={saving||!of.details.itemName} className={`${BTN_PRI} flex-1`}>{saving?'Placing…':'📋 Place Order'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ TRANSFER MODAL ══ */}
      <Modal open={showTransferForm} onClose={()=>setShowTransferForm(false)} title="↔️ Transfer Patient">
        <div className="space-y-4">
          {selAdmission&&(
            <div className="bg-slate-50 rounded-xl p-3 text-sm border">
              <p className="font-semibold text-slate-700">From: {selAdmission.wardName||selAdmission.ward_name||'—'} · Bed {selAdmission.bedNumber||selAdmission.bed_number||'—'}</p>
            </div>
          )}
          <Field label="Transfer Type">
            <select value={tf.transferType} onChange={e=>setTf(x=>({...x,transferType:e.target.value}))} className={INP}>
              {['INTERNAL','WARD_CHANGE','BED_CHANGE','ICU_TO_WARD','WARD_TO_ICU'].map(t=><option key={t}>{t.replace(/_/g,' ')}</option>)}
            </select>
          </Field>
          <Field label="Target Ward" required>
            <select value={tf.toWardId} onChange={e=>{setTf(x=>({...x,toWardId:e.target.value,toBedId:''}));if(e.target.value)loadAvailBeds(e.target.value);}} className={INP}>
              <option value="">Select Ward</option>
              {wards.filter((w:any)=>w.id!==selAdmission?.wardId&&w.id!==selAdmission?.ward_id).map((w:any)=>(
                <option key={w.id} value={w.id}>{w.name||w.wardCode} ({w.wardType})</option>
              ))}
            </select>
          </Field>
          {tf.toWardId&&(
            <Field label="Target Bed">
              <select value={tf.toBedId} onChange={e=>setTf(x=>({...x,toBedId:e.target.value}))} className={INP}>
                <option value="">Any available bed</option>
                {availBeds.map((b:any)=><option key={b.id} value={b.id}>Bed {b.bedNumber||b.bed_number} ({b.bedType||b.bed_type})</option>)}
              </select>
            </Field>
          )}
          <Field label="Reason for Transfer"><textarea value={tf.transferReason} onChange={e=>setTf(x=>({...x,transferReason:e.target.value}))} rows={3} placeholder="Clinical reason for transfer..." className={`${INP} resize-y min-h-[80px]`}/></Field>
          <div className="flex gap-3">
            <button onClick={()=>setShowTransferForm(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={saveTransfer} disabled={saving||!tf.toWardId} className={`${BTN_PRI} flex-1`}>{saving?'Transferring…':'↔️ Confirm Transfer'}</button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
